import { useQuery } from '@apollo/client/react';
import { PageLoader } from '@optools/components/loading/page-loader';
import { Card } from '@optools/components/ui/card';
import { COPYRIGHT_STREAMS } from '@optools/services/operator-gateway/btc/queries';
import {
  CopyrightStreamProvision,
  MediaFeedType,
  MediaPipeline,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { compareAsc, parse, startOfDay } from 'date-fns';

import { CopyrightStreamItem } from './copyright-stream-item';
import { ProvisionCopyrightStream } from './provision-copyright-stream';

export type CopyrightStreamProps = CopyrightStreamProvision & Pick<MediaPipeline, 'feedType'>;

const FeedTypeMapping: Record<string, string> = {
  AD: MediaFeedType.AWAY,
  HD: MediaFeedType.HOME,
  [MediaFeedType.AWAY]: 'AD',
  [MediaFeedType.HOME]: 'HD',
  [MediaFeedType.NATIONAL]: 'ND',
  ND: MediaFeedType.NATIONAL,
};

export function CopyrightStream({
  awayTriCode,
  feedType,
  gamePk,
  gameStartTime,
  homeTriCode,
  scheduleDate,
}: CopyrightStreamProps) {
  const { data, loading } = useQuery(COPYRIGHT_STREAMS, {
    fetchPolicy: 'cache-first',
    variables: { gamePk, scheduleDate },
  });

  const stream = data?.copyrightStreams?.find(
    (stream) => stream.gamePk === gamePk && feedType === FeedTypeMapping[stream.feedType],
  );

  const pipelineDate = parse(scheduleDate, 'yyyy-MM-dd', new Date());
  // Checks if the current date (first arg) comes before or after the scheduled date (second arg), to determine if the
  // copyright stream has expired.
  const expired = compareAsc(startOfDay(new Date()), pipelineDate) > 0;

  return (
    <section>
      <h2 className="mb-2 text-lg font-bold">Copyright Stream</h2>

      {loading && (
        <Card className="relative min-h-32">
          <PageLoader />
        </Card>
      )}

      {!loading &&
        (stream ? (
          <CopyrightStreamItem key={stream.id} stream={stream} />
        ) : (
          <Card className="flex min-h-32 animate-fade-in flex-col items-center justify-center gap-2 rounded border border-dashed p-8">
            {expired ? (
              <h1 className="text-sm">The Copyright Stream is no Longer Available</h1>
            ) : (
              <ProvisionCopyrightStream
                awayTriCode={awayTriCode}
                feedType={FeedTypeMapping[feedType]}
                gamePk={gamePk}
                gameStartTime={gameStartTime}
                homeTriCode={homeTriCode}
                scheduleDate={scheduleDate}
              />
            )}
          </Card>
        ))}
    </section>
  );
}
